/** tm/legal/TermChips.tsx — 검색어 칩 입력 (Enter·쉼표로 추가, × 삭제) + 동의어 제안 「+ 중소벤처기업진흥공단」 */
import { useMemo, useState } from 'react';
import { C, T, R, IMEText } from '../components/ui';
import { labelStyle, MUTED } from './legalTheme';
import type { SynonymGroups } from './legalTypes';

interface Props {
  label: string; hint?: string; value: string[]; onChange: (v: string[]) => void;
  synonyms: SynonymGroups; placeholder?: string; validate?: (v: string) => string | null;
  /** 제안하지 않을 말 (예: 제목 검색어 칸의 약칭) */
  exclude?: string[];
}

const clean = (s: string) => s.replace(/\s+/g, ' ').trim();

/** 칩·입력 중인 글자와 같은 뜻의 단어 중 아직 없는 것 */
export function suggestFor(value: string[], draft: string, groups: SynonymGroups, exclude: string[] = []): string[] {
  const have = new Set(value);
  const skip = new Set(exclude);
  const d = clean(draft);
  const out: string[] = [];
  for (const g of groups) {
    const hit = g.some(w => have.has(w) || (d.length >= 2 && (w.includes(d) || d.includes(w))));
    if (!hit) continue;
    for (const w of g) if (!have.has(w) && !skip.has(w) && w !== d && !out.includes(w)) out.push(w);
  }
  return out.slice(0, 10);
}

export default function TermChips({ label, hint, value, onChange, synonyms, placeholder, validate, exclude }: Props) {
  const [draft, setDraft] = useState('');
  const [err, setErr] = useState('');
  const sugg = useMemo(() => suggestFor(value, draft, synonyms, exclude), [value, draft, synonyms, exclude]);
  const add = (raw: string) => {
    const words = raw.split(/[,\n]/).map(clean).filter(Boolean);
    const next = [...value];
    for (const w of words) {
      const bad = validate?.(w);
      if (bad) { setErr(bad); return; }
      if (!next.includes(w)) next.push(w);
    }
    setErr(''); onChange(next); setDraft('');
  };
  return (
    <div style={{ marginBottom: 12 }}>
      <span style={labelStyle}>{label}{hint && <span style={{ fontWeight: 400, color: MUTED, marginLeft: 6 }}>{hint}</span>}</span>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, alignItems: 'center', border: `1px solid ${err ? C.red : C.border}`, borderRadius: R.sm, padding: 5, background: C.white }}>
        {value.map(w => (
          <span key={w} style={{ display: 'inline-flex', alignItems: 'center', gap: 2, padding: '2px 3px 2px 9px', borderRadius: 12, background: C.accentLt, color: C.accent, fontSize: T.sm, fontWeight: 600 }}>
            {w}
            <button type="button" className="lw-f" aria-label={`${w} 삭제`} onClick={() => onChange(value.filter(x => x !== w))}
              style={{ border: 0, background: 'transparent', color: C.accent, cursor: 'pointer', fontSize: T.sm, lineHeight: 1, padding: '0 4px' }}>×</button>
          </span>
        ))}
        <IMEText value={draft} onChange={v => { setDraft(v); if (err) setErr(''); }} placeholder={value.length ? '' : placeholder || '입력 후 Enter'} aria-label={label}
          onKeyDown={e => {
            if (e.nativeEvent.isComposing) return;
            if ((e.key === 'Enter' || e.key === ',') && clean(draft)) { e.preventDefault(); add(draft); }
            else if (e.key === 'Backspace' && !draft && value.length) onChange(value.slice(0, -1));
          }}
          onBlur={() => { if (clean(draft)) add(draft); }}
          style={{ flex: 1, minWidth: 120, border: 0, outline: 'none', fontSize: T.sm, fontFamily: 'inherit', padding: '3px 4px' }} />
      </div>
      {err && <div role="alert" style={{ fontSize: T.sm, color: C.red, marginTop: 3 }}>{err}</div>}
      {sugg.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 5, alignItems: 'center' }}>
          <span style={{ fontSize: T.sm, color: MUTED }}>같은 뜻</span>
          {sugg.map(w => (
            <button key={w} type="button" className="lw-f" onMouseDown={e => e.preventDefault()} onClick={() => { onChange([...value, w]); }}
              style={{ border: `1px dashed ${C.borderDk}`, background: C.white, color: C.sub, borderRadius: 12, padding: '1px 8px', fontSize: T.sm, cursor: 'pointer', fontFamily: 'inherit' }}>
              + {w}
            </button>
          ))}
          {sugg.length > 1 && (
            <button type="button" className="lw-f" onClick={() => onChange([...value, ...sugg])}
              style={{ border: 0, background: 'transparent', color: C.accent, fontSize: T.sm, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600 }}>모두 추가</button>
          )}
        </div>
      )}
    </div>
  );
}
